import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, ShieldCheck, FileCheck2, UserCheck, Mail, Phone, ArrowRight, Users } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1587271636175-90d58cdad458?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const DETAIL = "https://images.unsplash.com/photo-1603262110263-fb0112e7cc33?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function Landing() {
  const { t, lang, pick } = useLang();
  const { user } = useAuth();
  const [ev, setEv] = useState(null);

  useEffect(() => {
    api.get("/event-settings").then(({ data }) => setEv(data)).catch(() => {});
  }, []);

  const applyTo = user ? (user.role === "admin" ? "/admin" : "/apply") : "/register";
  const eligibility = ev ? (lang === "hi" ? ev.eligibility_hi : ev.eligibility_en) || [] : [];

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF6F0]">
      <Navbar />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Traditional Indian wedding ceremony" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <motion.div {...fade} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-400/20 border border-amber-300/40 px-4 py-1.5 text-amber-100 text-xs uppercase tracking-[0.2em] backdrop-blur-sm">
              <Users className="h-3.5 w-3.5" /> {t("club_name")}
            </span>
            <h1 className="mt-5 font-serif text-4xl sm:text-6xl lg:text-7xl text-white leading-tight">
              {ev ? pick(ev, "event_name") : "101 " + (lang === "hi" ? "कन्या विवाह" : "Kanya Vivah")}
            </h1>
            <p className="mt-4 text-lg text-amber-50/90 max-w-xl">
              {ev ? pick(ev, "tagline") : t("process_1_d")}
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-amber-50">
              <span className="inline-flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[#D4AF37]" /> {ev ? ev.event_date : t("event_on")}</span>
              <span className="inline-flex items-center gap-2"><MapPin className="h-5 w-5 text-[#D4AF37]" /> {ev ? pick(ev, "city") : "Jaipur"}</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={applyTo} data-testid="hero-apply-button">
                <Button size="lg" className="bg-[#8B1E2B] hover:bg-[#6f1622] text-white text-base px-7 py-6 rounded-xl shadow-lg hover:-translate-y-0.5 transition-all">
                  {t("apply_now")} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#process">
                <Button size="lg" variant="outline" className="bg-white/10 text-white border-white/40 hover:bg-white/20 text-base px-7 py-6 rounded-xl backdrop-blur-sm">
                  {t("nav_process")}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid gap-10 lg:grid-cols-2 items-center">
        <motion.div {...fade}>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-700">{t("about_title")}</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-[#8B1E2B]">{ev ? pick(ev, "event_name") : "101 Kanya Vivah"}</h2>
          <p className="mt-4 text-slate-700 leading-relaxed">{ev ? pick(ev, "about") : ""}</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <InfoCard icon={<CalendarDays className="h-5 w-5" />} label={t("date")} value={ev ? ev.event_date : "2027-03-10"} />
            <InfoCard icon={<MapPin className="h-5 w-5" />} label={t("venue")} value={ev ? (pick(ev, "venue") || pick(ev, "city")) : "Jaipur"} />
          </div>
        </motion.div>
        <motion.div {...fade} className="relative">
          {DETAIL && <img src={DETAIL} alt="Jaipur heritage" className="rounded-2xl shadow-[0_8px_30px_rgba(212,175,55,0.25)] object-cover w-full h-[360px]" />}
        </motion.div>
      </section>

      {/* Process */}
      <section id="process" className="bg-white/70 border-y border-amber-500/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#8B1E2B] text-center">{t("process_title")}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { icon: <UserCheck className="h-6 w-6" />, t: t("process_1_t"), d: t("process_1_d") },
              { icon: <FileCheck2 className="h-6 w-6" />, t: t("process_2_t"), d: t("process_2_d") },
              { icon: <ShieldCheck className="h-6 w-6" />, t: t("process_3_t"), d: t("process_3_d") },
            ].map((s, i) => (
              <motion.div key={i} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl bg-white border border-amber-500/20 p-7 shadow-[0_4px_20px_-4px_rgba(139,30,43,0.08)] hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(139,30,43,0.15)] transition-all">
                <div className="grid place-items-center h-12 w-12 rounded-xl bg-[#8B1E2B]/10 text-[#8B1E2B]">{s.icon}</div>
                <div className="mt-4 text-xs font-bold text-amber-700">STEP {i + 1}</div>
                <h3 className="mt-1 font-serif text-xl text-slate-900">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section id="eligibility" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#8B1E2B]">{t("eligibility_title")}</h2>
        <ul className="mt-6 grid gap-4 sm:grid-cols-2">
          {eligibility.map((e, i) => (
            <li key={i} className="flex items-start gap-3 rounded-xl bg-white border border-amber-500/20 p-5 shadow-sm">
              <span className="grid place-items-center h-7 w-7 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5"><UserCheck className="h-4 w-4" /></span>
              <span className="text-slate-700 text-sm leading-relaxed">{e}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Privacy */}
      <section id="privacy" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-6 sm:p-8">
          <h3 className="font-serif text-2xl text-[#0A2540] flex items-center gap-2"><ShieldCheck className="h-6 w-6" /> {t("privacy_title")}</h3>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed" data-testid="privacy-notice-text">{t("privacy_body")}</p>
        </div>
      </section>

      {/* Contact + CTA */}
      <section id="contact" className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-[#0A2540] text-white p-8 sm:p-12 grid gap-8 lg:grid-cols-2 items-center">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#D4AF37]">{t("apply_now")}</h2>
            <p className="mt-3 text-amber-50/80">{t("process_2_d")}</p>
            <Link to={applyTo} data-testid="cta-apply-button" className="inline-block mt-6">
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#c39f2f] text-[#0A2540] font-semibold text-base px-7 py-6 rounded-xl">
                {t("apply_now")} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          <div className="lg:justify-self-end w-full lg:max-w-sm rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-xl text-white">{t("contact_title")}</h3>
            <div className="mt-4 space-y-3 text-amber-50/90 text-sm">
              <a href={`mailto:${ev?.contact_email || "navneet.sogani@gmail.com"}`} className="flex items-center gap-3 hover:text-[#D4AF37]" data-testid="contact-email">
                <Mail className="h-4 w-4 text-[#D4AF37]" /> {ev?.contact_email || "navneet.sogani@gmail.com"}
              </a>
              {ev?.contact_phone ? (
                <a href={`tel:${ev.contact_phone}`} className="flex items-center gap-3 hover:text-[#D4AF37]" data-testid="contact-phone">
                  <Phone className="h-4 w-4 text-[#D4AF37]" /> {ev.contact_phone}
                </a>
              ) : (
                <span className="flex items-center gap-3 text-amber-50/50"><Phone className="h-4 w-4" /> Phone to be announced</span>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function InfoCard({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-amber-500/20 bg-white p-4 flex items-center gap-3">
      <span className="grid place-items-center h-10 w-10 rounded-lg bg-[#D4AF37]/15 text-[#8B1E2B]">{icon}</span>
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="font-semibold text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}
